import { useDeferredDateFieldInvalidity } from '@/hooks';
import { formatDateForFilterValue, getSectionsState, isBlurLeavingField } from '@/util';
import {
  DateValidationError,
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import type { FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { FieldMetaData } from './types';

// Parent notification (with value + error details) happens in the handlers
// below, so the hook's Apply-reporting callback has nothing extra to do.
const noopReportInvalid = () => {};

type DatePickerTFCProps<ValueIfNull extends undefined | null> = Omit<
  MuiDatePickerProps,
  'value' | 'onChange'
> & {
  valueIfNull: ValueIfNull;
  onChange: (value: string | ValueIfNull, fieldMetaData: FieldMetaData) => void;
  isEmptyValid?: boolean;
  /** ISO date string for Restart pre-fill */
  initialValue?: string | null;
};

export default function DatePickerTFC<ValueIfNull extends undefined | null>({
  onChange,
  valueIfNull,
  isEmptyValid = false,
  initialValue,
  ...props
}: DatePickerTFCProps<ValueIfNull>) {
  const [value, setValue] = useState<Date | null>(
    initialValue ? new Date(initialValue) : null
  );
  // Last value reported to the parent as valid (formatted ISO string), so a
  // blur on an incomplete date doesn't wipe it from the tree.
  const lastCommittedValueRef = useRef<string | ValueIfNull | null>(null);

  const isFocusedRef = useRef(false);
  // MUI emits onChange(null) both for a fully cleared field and when one section of
  // a valid date is cleared (leaving it partial). While focused we can't tell which,
  // so parent notification is deferred until blur, keeping the committed value.
  const pendingNullRef = useRef(false);

  const { handleError, handleChangeValidation, handleBlur, errorReason } =
    useDeferredDateFieldInvalidity(noopReportInvalid);

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- seed picker from initialValue when empty */
  useEffect(() => {
    if (initialValue && value === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-derived-state -- seed picker from initialValue when empty
      setValue(new Date(initialValue));
    }
    // Only apply when empty - don't overwrite user edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleOnChange = (
    newValue: Date | null,
    { validationError }: PickerChangeHandlerContext<DateValidationError>
  ) => {
    setValue(newValue);

    if (newValue === null && !validationError && isFocusedRef.current) {
      pendingNullRef.current = true;
      return;
    }

    pendingNullRef.current = false;
    handleChangeValidation(validationError ?? null);

    if (validationError) {
      onChange(valueIfNull, {
        state: 'invalid',
        error: {
          reason: validationError,
          helperText: 'Please select a valid date',
        },
      });
    } else if (newValue === null) {
      lastCommittedValueRef.current = null;
      onChange(valueIfNull, { state: isEmptyValid ? 'valid' : 'empty', error: null });
    } else {
      const formattedValue = formatDateForFilterValue(newValue, valueIfNull);
      lastCommittedValueRef.current = formattedValue;
      onChange(formattedValue, { state: 'valid', error: null });
    }
  };

  const textFieldSlotProps =
    typeof props.slotProps?.textField === 'object' ? props.slotProps.textField : undefined;

  const handleTextFieldFocus = (event: FocusEvent<HTMLDivElement>) => {
    textFieldSlotProps?.onFocus?.(event);
    isFocusedRef.current = true;
  };

  const handleTextFieldBlur = (event: FocusEvent<HTMLDivElement>) => {
    textFieldSlotProps?.onBlur?.(event);
    if (!isBlurLeavingField(event)) return;
    isFocusedRef.current = false;

    const sectionsState = getSectionsState(event.currentTarget);
    let reason = errorReason;

    if (sectionsState === 'partial') {
      reason = reason ?? 'invalidDate';
      handleError(reason);
    } else if (sectionsState === 'empty' && (pendingNullRef.current || errorReason)) {
      // Genuinely empty field: either a deferred onChange(null), or a previously
      // partial/invalid field cleared (null -> null, so MUI emits no onChange)
      pendingNullRef.current = false;
      handleChangeValidation(null);
      lastCommittedValueRef.current = null;
      onChange(valueIfNull, { state: isEmptyValid ? 'valid' : 'empty', error: null });
      return;
    }

    handleBlur();
    if (reason) {
      onChange(lastCommittedValueRef.current ?? valueIfNull, {
        state: 'invalid',
        error: { reason, helperText: 'Please select a valid date' },
      });
    }
  };

  return (
    <MuiDatePicker
      label="Contacted from date"
      views={['year', 'month', 'day']}
      value={value}
      format="dd/MM/yyyy"
      onChange={handleOnChange}
      {...props}
      onError={(reason, errValue) => {
        handleError(reason);
        props.onError?.(reason, errValue);
      }}
      slotProps={{
        ...props.slotProps,
        textField: {
          color: 'secondary',
          ...textFieldSlotProps,
          onFocus: handleTextFieldFocus,
          onBlur: handleTextFieldBlur,
        },
      }}
    />
  );
}
